import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type Product = Tables<"products">;
export type ProductInsert = TablesInsert<"products">;
export type ProductUpdate = TablesUpdate<"products">;

export function useProducts(filters?: {
  category?: string;
  search?: string;
  type?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["products", filters],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let q = supabase
        .from("products")
        .select("*")
        .neq("type", "variation")
        .eq("published", true)
        .order("name");

      if (filters?.category) {
        q = q.ilike("categories", `%${filters.category}%`);
      }
      if (filters?.search) {
        q = q.ilike("name", `%${filters.search}%`);
      }
      if (filters?.limit) {
        q = q.limit(filters.limit);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export function useAllAdminProducts(search?: string) {
  return useQuery({
    queryKey: ["admin-products", search],
    queryFn: async () => {
      let q = supabase
        .from("products")
        .select("*")
        .neq("type", "variation")
        .order("name");

      if (search) {
        q = q.or(`name.ilike.%${search}%,sku.ilike.%${search}%`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("slug", slug)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });
}

export function useProductVariations(parent: Pick<Product, "id" | "sku"> | null) {
  return useQuery({
    queryKey: ["variations", parent?.id],
    queryFn: async () => {
      if (!parent) return [];

      // Try parent_id first
      const { data: byId, error: e1 } = await supabase
        .from("products")
        .select("*")
        .eq("parent_id", parent.id)
        .eq("type", "variation");
      if (e1) throw e1;
      if (byId && byId.length > 0) return byId;

      // Fallback: match by parent_sku = parent's sku
      if (!parent.sku) return [];
      const { data: bySku, error: e2 } = await supabase
        .from("products")
        .select("*")
        .eq("parent_sku", parent.sku)
        .eq("type", "variation");
      if (e2) throw e2;
      return bySku ?? [];
    },
    enabled: !!parent,
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: ProductUpdate & { id: number }) => {
      const { data, error } = await supabase
        .from("products")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });
}

export function useBulkInsertProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (products: ProductInsert[]) => {
      const { data, error } = await supabase
        .from("products")
        .upsert(products, { onConflict: "sku", ignoreDuplicates: false })
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("categories")
        .neq("type", "variation")
        .eq("published", true);
      if (error) throw error;

      const cats = new Set<string>();
      data.forEach((p) => {
        if (p.categories) {
          // Split WooCommerce hierarchical categories
          p.categories.split(",").forEach((c) => {
            const trimmed = c.trim().split(">").pop()?.trim();
            if (trimmed) cats.add(trimmed);
          });
        }
      });
      return [...cats].sort();
    },
  });
}

/** Upgrade http:// image URLs to https:// to avoid mixed-content blocking */
function toHttps(url: string): string {
  return url.startsWith("http://") ? url.replace("http://", "https://") : url;
}

/**
 * Wrap a Supabase Storage public URL with the image transformation endpoint
 * to serve a resized, compressed WebP variant. No-op for non-Supabase URLs.
 */
export function optimizedImage(url: string, width: number, quality = 70): string {
  if (!url) return url;
  if (!url.includes("/storage/v1/object/public/")) return url;
  const transformed = url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/"
  );
  const sep = transformed.includes("?") ? "&" : "?";
  return `${transformed}${sep}width=${width}&quality=${quality}&resize=cover`;
}

export function getFirstImage(images: string | null): string {
  if (!images) return "/placeholder.svg";
  const first = images.split(",")[0].trim();
  return first ? toHttps(first) : "/placeholder.svg";
}

export function getAllImages(images: string | null): string[] {
  if (!images) return [];
  return images.split(",").map((s) => s.trim()).filter(Boolean).map(toHttps);
}

/** Default placeholder price for products without a set price */
export const DEFAULT_PRICE = 6.99;

/** Returns the effective price for a product, falling back to DEFAULT_PRICE */
export function getEffectivePrice(product: Pick<Product, "sale_price" | "regular_price">): number {
  return product.sale_price || product.regular_price || DEFAULT_PRICE;
}

/** Fetches the 5 most expensive published products */
export function useFeaturedProducts() {
  return useQuery({
    queryKey: ["featured-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .neq("type", "variation")
        .eq("published", true)
        .not("regular_price", "is", null)
        .order("regular_price", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
  });
}
